import styled, { css } from 'styled-components';
import colors from '../../../helpers/colors';
import fonts from '../../../helpers/fonts';
import mediaQuery from '../../../helpers/mediaQuery';
import spacing from '../../../helpers/spacing';
import GridContainer from '../../GridContainer';
import Image from '../../Image';
import LazyImg from '../../LazyImg';

/* desktop sizes */
const HERO_HEIGHT = '638px';
const TEXT_OFFSET = '424px';
const TRAPEZOID_OFFSET = '272px';
const TRAPEZOID_HEIGHT = '119px';
const BIG_TRIANGLE_HEIGHT = '248px';
const SMALL_TRIANGLE_HEIGHT = '215px';
const SMALL_TRIANGLE_WIDTH = '326px';

/* tablet sizes */
const HERO_HEIGHT_TABLET = '589px';
const TEXT_OFFSET_TABLET = '390px';
const TRAPEZOID_OFFSET_TABLET = '234px';
const TRAPEZOID_HEIGHT_TABLET = '178px';
const BIG_TRIANGLE_HEIGHT_TABLET = '180px';
const SMALL_TRIANGLE_HEIGHT_TABLET = '315px';
const SMALL_TRIANGLE_WIDTH_TABLET = '377px';

/* mobile sizes */
const HERO_HEIGHT_MOBILE = '489px';
const TEXT_OFFSET_MOBILE = '238px';
const TRAPEZOID_OFFSET_MOBILE = '154px';
const TRAPEZOID_HEIGHT_MOBILE = '250px';
const BIG_TRIANGLE_HEIGHT_MOBILE = '86px';
const SMALL_TRIANGLE_HEIGHT_MOBILE = '190px';
const SMALL_TRIANGLE_WIDTH_MOBILE = '100vw';

export const ImageWrapper = styled.div`
  position: absolute;
  width: 100vw;
  right: 0;
  z-index: -3;
  top: 0;

  @media screen and (min-width: 1880px) {
    width: 1880px;
  }
`;

export const MainImage = styled(Image)`
  object-fit: cover;
  min-height: calc(${TRAPEZOID_OFFSET_MOBILE} + ${BIG_TRIANGLE_HEIGHT_MOBILE});
  max-height: ${HERO_HEIGHT_MOBILE};

  ${mediaQuery.tabletPortrait`
    min-height: calc(${TRAPEZOID_OFFSET_TABLET} + ${BIG_TRIANGLE_HEIGHT_TABLET});
    max-height: ${HERO_HEIGHT_TABLET};
  `}

  ${mediaQuery.tabletLandscape`
    min-height: calc(${TRAPEZOID_OFFSET} + ${BIG_TRIANGLE_HEIGHT});
    max-height: ${HERO_HEIGHT};
  `}
`;

export const Banner = styled.div`
  position: absolute;
  background: ${colors.core02};
  width: 100vw;
  height: ${spacing(8)};
  max-width: 1880px;
  left: 0;
  right: 0;
  top: ${HERO_HEIGHT_MOBILE};

  ${mediaQuery.tabletPortrait`
    top: ${HERO_HEIGHT_TABLET};
  `}

  ${mediaQuery.tabletLandscape`
    top: ${HERO_HEIGHT};
  `}
`;

export const StyledGridContainer = styled(GridContainer)`
  display: flex;
  justify-content: flex-end;
`;

export const BannerImage = styled(LazyImg)`
  margin: ${spacing(1.75)} 0 ${spacing(1.75)} auto;
  object-fit: contain;
  border-radius: ${spacing(1)};
  height: ${spacing(4.5)};
`;

export const TextWrapper = styled.div`
  margin-top: ${TEXT_OFFSET_MOBILE};

  ${mediaQuery.tabletPortrait`
    margin-top: ${TEXT_OFFSET_TABLET};
  `}

  ${mediaQuery.tabletLandscape`
    margin-top: ${TEXT_OFFSET};
  `}
`;

export const Heading = styled.h1`
  ${fonts.headingXLarge}

  margin: ${spacing(4)} 0 0;
  ${mediaQuery.tabletPortrait`
    margin: ${spacing(6)} 0 0;
  `}

  & {
    color: ${colors.neutral08};
  }
`;

export const Subhead = styled.p`
  ${fonts.paragraph}
  margin: ${spacing(2)} 0 0;

  & {
    color: ${colors.neutral08};
  }

  ${mediaQuery.tabletPortrait`
    padding-right: ${spacing(8)};
  `}
`;

const bigTrapezoidStyles = css`
  content: ' ';
  position: absolute;
  left: 0;
  right: 0;
  height: ${TRAPEZOID_HEIGHT_MOBILE}; // height of 'square' part
  width: 0;
  z-index: -1;
  transform: scale(1, -1);
  top: ${TRAPEZOID_OFFSET_MOBILE};

  border-left: 100vw solid ${colors.core01}; // width of banner
  border-bottom: ${BIG_TRIANGLE_HEIGHT_MOBILE} solid transparent; // height of triangle tip

  ${mediaQuery.tabletPortrait`
    top: ${TRAPEZOID_OFFSET_TABLET};
    height: ${TRAPEZOID_HEIGHT_TABLET};
    border-bottom: ${BIG_TRIANGLE_HEIGHT_TABLET} solid transparent;
  `}

  ${mediaQuery.tabletLandscape`
    top: ${TRAPEZOID_OFFSET};
    height: ${TRAPEZOID_HEIGHT};
    border-bottom: ${BIG_TRIANGLE_HEIGHT} solid transparent;
  `}

  @media (min-width: 1880px) {
    border-left: 1880px solid ${colors.core01};
  }
`;

const smallTriangleStyles = css`
  content: ' ';
  position: absolute;
  top: calc(${TRAPEZOID_OFFSET_MOBILE} + (${BIG_TRIANGLE_HEIGHT_MOBILE} / 2));
  right: 0;
  height: 0;
  width: 0;
  z-index: -2;
  transform: scale(1, -1);

  border-top: ${SMALL_TRIANGLE_HEIGHT_MOBILE} solid ${colors.core02};
  border-left: ${SMALL_TRIANGLE_WIDTH_MOBILE} solid transparent;

  ${mediaQuery.tabletPortrait`
    top: calc(${HERO_HEIGHT_TABLET} - ${SMALL_TRIANGLE_HEIGHT_TABLET});
    border-top: ${SMALL_TRIANGLE_HEIGHT_TABLET} solid ${colors.core02};
    border-left: ${SMALL_TRIANGLE_WIDTH_TABLET} solid transparent;
  `}

  ${mediaQuery.tabletLandscape`
    top: calc(${HERO_HEIGHT} - ${TRAPEZOID_HEIGHT} - ${SMALL_TRIANGLE_HEIGHT});
    border-top: ${SMALL_TRIANGLE_HEIGHT} solid ${colors.core02};
    border-left: ${SMALL_TRIANGLE_WIDTH} solid transparent;
  `}
`;

export const OrigamiBackground = styled.div<{ banner?: boolean }>`
  position: relative;
  max-width: 1880px;

  ${({ banner }) =>
    banner
      ? css`
          height: calc(${spacing(8)} + ${HERO_HEIGHT_MOBILE});
        `
      : css`
          height: ${HERO_HEIGHT_MOBILE};
        `};

  /* takes background to full screen */
  margin: 0 calc(50% - 50vw) ${spacing(8)};
  /* resets the content back to the grid */
  padding: 0 calc(50vw - 50%);

  ${mediaQuery.tabletPortrait`
  ${({ banner }) =>
    banner
      ? css`
          height: calc(${spacing(8)} + ${HERO_HEIGHT_TABLET});
        `
      : css`
          height: ${HERO_HEIGHT_TABLET};
        `};
  `}

  ${mediaQuery.tabletLandscape`
    ${({ banner }) =>
      banner
        ? css`
            height: calc(${spacing(8)} + ${HERO_HEIGHT});
          `
        : css`
            height: ${HERO_HEIGHT};
          `};
  `}

  @media (min-width: 1880px) {
    // max margin and padding for max-width
    margin: ${({ theme }) =>
      `0 calc((${theme.layout.desktop.containerWidthPx ?? 1200}px - 1880px) / 2)`};
    margin-bottom: ${spacing(4)};
    padding: ${({ theme }) =>
      `0 calc((1880px - ${theme.layout.desktop.containerWidthPx ?? 1200}px) / 2)`};
  }

  /* Large trapezoid on the top */
  &::before {
    ${bigTrapezoidStyles}
  }

  /* Smaller triangle underneath */
  &::after {
    ${smallTriangleStyles}
  }
`;
